import { Button, Checkbox, Form, message, Modal, ModalProps, Radio } from 'antd';
import { SalesforceAccountSearch } from 'components/atoms/SalesforceAccountSearch';
import { RefSelectProps } from 'antd/lib/select';
import { useEffect, useRef, useState } from 'react';
import { getClientBySalesforceAccount } from 'api/salesforce';
import { useForm } from 'antd/es/form/Form';
import { useRecoilState } from 'recoil';
import { profileSwitchState } from 'recoil/atoms';
import { UserSwitchOutlined } from '@ant-design/icons';
import { SubscriptionLevel } from 'models/User.d';
import { useQueryClient } from '@tanstack/react-query';

interface Props extends ModalProps {
  onSwitchProfile: () => void;
}

export const SwitchProfileModal: React.FC<Props> = ({ onSwitchProfile, ...rest }: Props) => {
  const [form] = useForm();
  const salesForceClientRef = useRef<RefSelectProps>(null);

  // LOCAL STATE
  const [clientId, setClientId] = useState<number | undefined>(undefined);
  const [childOrgs, setChildOrgs] = useState<number>(0);
  const [account, setAccount] = useState<undefined | { label: string; value: string }>(undefined);

  // RECOIL STATE
  const [profileSwitch, setProfileSwitch] = useRecoilState(profileSwitchState);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (rest.open) {
      form.setFieldsValue({ subscriptionLevel: SubscriptionLevel.Gold, anonymous: false });
      setTimeout(() => salesForceClientRef.current?.focus(), 100);
    }
  }, [form, rest.open]);

  const invalidateTasks = async () => {
    queryClient.invalidateQueries(['my_tasks']).then();
  };

  const handleAccountChange = async (item?: { label: string; value: string }) => {
    if (!item) {
      setAccount(undefined);
      setClientId(undefined);
      return;
    }

    setAccount({ label: item.label, value: item.label });
    const clientResult: { id: number; childOrgs: number } | undefined = await getClientBySalesforceAccount(item.value);
    if (clientResult) {
      setClientId(clientResult.id);
      setChildOrgs(clientResult.childOrgs);
    } else {
      setClientId(undefined);
      setChildOrgs(0);
      await message.warning(`${item.label} has no NPI Digital client record`);
      setTimeout(() => salesForceClientRef.current?.focus(), 100);
      setAccount(undefined);
      setProfileSwitch(undefined);
      await invalidateTasks();
    }
  };

  const onOkHandler = async () => {
    if (!clientId || !account) return;

    const anonymous = form.getFieldValue('anonymous');
    const subscription_level_id = form.getFieldValue('subscriptionLevel');

    setProfileSwitch({
      id: clientId,
      name: account.label,
      anonymous: anonymous || false,
      subscription_level_id,
      childOrgs
    });
    await invalidateTasks();
    setClientId(undefined);
    setChildOrgs(0);
    form.resetFields();
    onSwitchProfile();
  };

  const switchBack = async () => {
    form.resetFields();
    setClientId(undefined);
    setChildOrgs(0);
    setTimeout(() => salesForceClientRef.current?.focus(), 100);
    setProfileSwitch(undefined);
    await invalidateTasks();
  };

  return (
    <Modal
      title="Switch Profile"
      style={{ width: 650 }}
      forceRender
      {...rest}
      okText="Switch Profile"
      cancelText="Close"
      okButtonProps={{ disabled: !clientId }}
      onOk={onOkHandler}
    >
      <Form form={form} component={false} initialValues={{ subscriptionLevel: SubscriptionLevel.Gold }}>
        <Form.Item name="account">
          <SalesforceAccountSearch
            placeholder="Search Salesforce for client name"
            ref={salesForceClientRef}
            style={{ width: '100%' }}
            onChange={handleAccountChange}
          />
        </Form.Item>
        <Form.Item name="subscriptionLevel" label="Subscription Level">
          <Radio.Group disabled={!account}>
            <Radio value={SubscriptionLevel.Basic}>Vantage Basic</Radio>
            <Radio value={SubscriptionLevel.Gold}>Vantage Pro</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item name="anonymous" valuePropName="checked">
          <Checkbox defaultChecked={false}>Make Client Anonymous</Checkbox>
        </Form.Item>
        {profileSwitch && (
          <Button
            icon={<UserSwitchOutlined />}
            type="primary"
            // size="small"
            onClick={switchBack}
            style={{ fontSize: 12 }}
          >
            Reset
          </Button>
        )}
      </Form>
    </Modal>
  );
};
